import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, PartyPopper, User, Briefcase } from 'lucide-react';
import styled from 'styled-components';
import { Form } from '@components/ui/Form';
import { Input, TextArea } from '@components/ui/Input';
import { Button } from '@components/ui/Button';
import Link from '@components/ui/Link';
import { usePublicPlans, useOnboardingClientMutation, useOnboardingCompanyMutation } from '@hooks/useAuthMutations';
import { getDashboardRoute } from '@utils/routing';
import { useAuthStore } from '@stores/auth.store';

const STEPS = [
    { key: 'choice', label: 'Select path' },
    { key: 'form', label: 'Set up' },
    { key: 'plans', label: 'Choose plan' },
];

// ============ LAYOUT: light gray full page ============
const PageRoot = styled(motion.div)`
    position: relative;
    width: 100%;
    min-height: 100vh;
    min-height: 100dvh;
    overflow: auto;
    background: #f5f5f5;
`;

const OnboardingInner = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 32px 24px 48px;
`;

// ============ STEPPER ============
const StepperWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
    margin-bottom: 32px;
    flex-shrink: 0;
`;

const StepperStepBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 0;
    padding: 0;
    margin: 0;
    background: none;
    border: none;
    cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
    opacity: ${({ $clickable }) => ($clickable ? 1 : 0.7)};
    &:hover {
        opacity: ${({ $clickable }) => ($clickable ? 1 : 0.7)};
    }
`;

const StepperStep = styled.div`
    display: flex;
    align-items: center;
    gap: 0;
`;

const StepperCircle = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    font-size: 0.875rem;
    font-weight: 600;
    background: ${({ $active, $done }) => ($active || $done ? '#8b5cf6' : 'transparent')};
    color: ${({ $active, $done }) => ($active || $done ? '#fff' : '#9ca3af')};
    border: 2px solid ${({ $active, $done }) => ($active || $done ? '#8b5cf6' : '#d1d5db')};
`;

const StepperLabel = styled.span`
    margin-left: 8px;
    font-size: 0.9375rem;
    font-weight: 500;
    color: ${({ $active, $done }) => ($active || $done ? '#8b5cf6' : '#9ca3af')};
`;

const StepperConnector = styled.span`
    width: 48px;
    height: 2px;
    background: #e5e7eb;
    margin: 0 4px;
    flex-shrink: 0;
    @media (max-width: 640px) {
        width: 24px;
    }
`;

// ============ MAIN CONTENT CARD (white, centered) ============
const ContentCard = styled(motion.div)`
    width: 100%;
    max-width: 560px;
    background: #fff;
    border-radius: 16px;
    padding: 32px 28px 28px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const CardTitle = styled.h2`
    font-size: 1.375rem;
    font-weight: 700;
    color: #374151;
    margin: 0 0 8px 0;
`;

const CardSubtitle = styled.p`
    font-size: 0.9375rem;
    color: #6b7280;
    line-height: 1.5;
    margin: 0 0 24px 0;
`;

// ============ STEP 1: Path option cards ============
const PathOptionsWrap = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 28px;
    @media (max-width: 520px) {
        grid-template-columns: 1fr;
    }
`;

const PathOptionCard = styled(motion.button)`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    padding: 20px;
    background: #f3f4f6;
    border: 2px solid transparent;
    border-radius: 12px;
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    transition:
        transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
        box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1),
        border-color 0.2s ease,
        background 0.2s ease;

    &:hover {
        transform: translateY(-4px);
        box-shadow:
            0 12px 24px rgba(0, 0, 0, 0.08),
            0 4px 8px rgba(0, 0, 0, 0.04);
        background: #e5e7eb;
    }

    &:active {
        transform: translateY(-2px);
        box-shadow: 0 6px 12px rgba(0, 0, 0, 0.06);
    }

    ${({ $selected }) =>
        $selected &&
        `
        border-color: #8b5cf6;
        background: #f5f3ff;
        box-shadow: 0 4px 12px rgba(139, 92, 246, 0.15);

        &:hover {
            background: #ede9fe;
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(139, 92, 246, 0.2);
        }
    `}
`;

const PathOptionIcon = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    margin-bottom: 12px;
    color: ${({ $selected }) => ($selected ? '#8b5cf6' : '#6b7280')};
    transition:
        color 0.2s ease,
        transform 0.2s ease;
    ${PathOptionCard}:hover & {
        transform: scale(1.05);
        color: ${({ $selected }) => ($selected ? '#7c3aed' : '#4b5563')};
    }
`;

const PathOptionTitle = styled.span`
    font-size: 1rem;
    font-weight: 700;
    color: #374151;
    margin-bottom: 4px;
    display: block;
    transition: color 0.2s ease;
    ${PathOptionCard}:hover & {
        color: #1f2937;
    }
`;

const PathOptionDesc = styled.span`
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.45;
    transition: color 0.2s ease;
    ${PathOptionCard}:hover & {
        color: #4b5563;
    }
`;

const ProceedButtonWrap = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
`;

const ProceedButtonStyled = styled(Button)`
    min-width: 140px;
    height: 48px !important;
    padding: 14px 24px !important;
    font-size: 1rem !important;
    font-weight: 600 !important;
    border-radius: 10px !important;
    background: ${({ $disabled }) => ($disabled ? '#e5e7eb' : '#8b5cf6')} !important;
    color: ${({ $disabled }) => ($disabled ? '#9ca3af' : '#fff')} !important;
    border: none !important;
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    &:hover:not(:disabled) {
        background: #7c3aed !important;
    }
`;

// ============ HEADER: Sign in link only (top right) ============
const Header = styled.header`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 20px 32px;
    pointer-events: auto;
    @media (max-width: 640px) {
        padding: 16px 20px;
    }
`;

// ============ STEP 3: Plan cards in a row (horizontal, side by side) inside ContentCard ============
const PlanCardsWrap = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px;
    width: 100%;
    margin: 0 0 28px 0;
    justify-content: flex-start;
`;

const PLAN_CARD_COLORS = [
    { header: '#FACC15', text: '#B45309', label: 'Free' },
    { header: '#3B82F6', text: '#1D4ED8', label: 'Starter' },
    { header: '#8B5CF6', text: '#5B21B6', label: 'Pro' },
    { header: '#EC4899', text: '#BE185D', label: 'Enterprise' },
];

const PlanCard = styled(motion.button)`
    flex: 1 1 0;
    min-width: 160px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    text-align: left;
    border-radius: 12px;
    overflow: hidden;
    border: none;
    background: #fff;
    cursor: pointer;
    box-shadow: ${({ $selected }) =>
        $selected
            ? '0 8px 24px rgba(0, 0, 0, 0.12), 0 4px 14px rgba(139, 92, 246, 0.25)'
            : '0 1px 3px rgba(0, 0, 0, 0.06)'};
    transform: ${({ $selected }) => ($selected ? 'translateY(-4px)' : 'translateY(0)')};
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
    padding: 0;
    min-height: 0;
    &:hover {
        transform: translateY(-4px);
        box-shadow:
            0 8px 24px rgba(0, 0, 0, 0.12),
            0 4px 14px rgba(139, 92, 246, 0.15);
    }
`;

const PlanCardHeader = styled.div`
    padding: 14px 16px;
    background: ${({ $color }) => $color || '#64748b'};
    color: #fff;
    font-size: 0.8125rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    border-radius: 12px 12px 0 0;
`;

const PlanCardBody = styled.div`
    padding: 16px;
`;

const PlanCardPrice = styled.div`
    margin-bottom: 8px;
    font-size: 1.5rem;
    font-weight: 700;
    color: ${({ $color }) => $color || '#1e293b'};
    line-height: 1.2;
`;

const PlanCardPriceUnit = styled.span`
    font-size: 0.75rem;
    font-weight: 500;
    color: #6b7280;
`;

const PlanCardDescription = styled.p`
    margin: 0 0 12px 0;
    font-size: 0.8125rem;
    font-weight: 400;
    color: #6b7280;
    line-height: 1.45;
`;

const PlanCardFeatures = styled.ul`
    padding: 0 0 0 18px;
    margin: 0;
    list-style: disc;
    font-size: 0.75rem;
    font-weight: 400;
    color: #6b7280;
    line-height: 1.5;
`;

const PlanCardFeature = styled.li`
    margin-bottom: 4px;
    padding-left: 4px;
    &:last-child {
        margin-bottom: 0;
    }
`;

const SuccessWrap = styled(motion.div)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    text-align: center;
    position: relative;
    z-index: 1;
`;

const BackLink = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 24px;
    padding: 0;
    font-size: 0.9375rem;
    color: #8b5cf6;
    background: none;
    border: none;
    cursor: pointer;
    font-weight: 500;
    &:hover {
        color: #7c3aed;
    }
`;

const FormTitle = styled.h2`
    font-size: 1.375rem;
    font-weight: 700;
    color: #1e1b4b;
    margin: 0 0 8px 0;
`;

const FormSubtitle = styled.p`
    font-size: 0.9375rem;
    color: rgba(30, 27, 75, 0.6);
    margin: 0 0 24px 0;
    line-height: 1.5;
`;

const SuccessIcon = styled(motion.div)`
    width: 88px;
    height: 88px;
    border-radius: 50%;
    background: linear-gradient(145deg, #ede9fe 0%, #c4b5fd 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5b21b6;
    margin-bottom: 28px;
`;

const SuccessTitle = styled.h2`
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e1b4b;
    margin: 0 0 12px 0;
`;

const SuccessText = styled.p`
    font-size: 0.9375rem;
    color: rgba(30, 27, 75, 0.65);
    margin: 0 0 32px 0;
    line-height: 1.5;
`;

const SuccessScreen = styled(motion.div)`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 32px;
    overflow: auto;
    background: #f5f5f5;
`;

const Onboarding = () => {
    const navigate = useNavigate();
    const role = useAuthStore((state) => state.role ?? state.user?.role);
    const [step, setStep] = useState('choice');
    const [type, setType] = useState(null);
    const [formStep, setFormStep] = useState(0);
    const [detailsValues, setDetailsValues] = useState({ name: '', description: '' });
    const [form] = Form.useForm();

    const { data: plans = [], isLoading: plansLoading } = usePublicPlans();
    const clientMutation = useOnboardingClientMutation();
    const companyMutation = useOnboardingCompanyMutation();
    const loading = clientMutation.isPending || companyMutation.isPending;

    const handleSelect = (selectedType) => {
        setType(selectedType);
        setFormStep(0);
        setStep('form');
    };

    const goToStep = (stepIndex) => {
        if (stepIndex === 0) {
            setStep('choice');
            setFormStep(0);
        } else if (stepIndex === 1) {
            setStep('form');
            setFormStep(0);
        } else {
            setStep('form');
            setFormStep(1);
        }
    };

    const handleDetailsContinue = async () => {
        try {
            const values = await form.validateFields(['name', 'description']);
            setDetailsValues({ name: values.name ?? '', description: values.description ?? '' });
            setFormStep(1);
        } catch {
            // validation failed
        }
    };

    const onFinish = async (values) => {
        const planId = values.planId || null;
        const name = values.name ?? detailsValues.name;
        const description = values.description ?? detailsValues.description;
        try {
            if (type === 'client') {
                await clientMutation.mutateAsync({
                    businessName: name,
                    businessDetails: description,
                    planId,
                });
            } else {
                await companyMutation.mutateAsync({
                    companyName: name,
                    companyDetails: description,
                    planId,
                });
            }
            setStep('success');
            setTimeout(() => {
                navigate(getDashboardRoute(role) || '/dashboard', { replace: true });
            }, 2500);
        } catch {
            // Error shown by mutation onError
        }
    };

    const nameLabel = type === 'client' ? 'Business name' : 'Company name';
    const descLabel = type === 'client' ? 'Business details' : 'Company details';
    const watchedPlanId = Form.useWatch('planId', form);

    const currentStepIndex = step === 'choice' ? 0 : formStep === 0 ? 1 : 2;

    return (
        <PageRoot initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.35 }}>
            <Header>
                <Link to="/sign-in">Sign in</Link>
            </Header>

            {(step === 'choice' || step === 'form') && (
                <OnboardingInner key="onboarding">
                    <StepperWrap>
                        {STEPS.map((s, i) => {
                            const isClickable = i <= currentStepIndex;
                            const isActive = i === currentStepIndex;
                            const isDone = i < currentStepIndex;
                            const StepWrapper = isClickable ? StepperStepBtn : StepperStep;
                            return (
                                <StepWrapper
                                    key={s.key}
                                    as={isClickable ? 'button' : 'div'}
                                    type={isClickable ? 'button' : undefined}
                                    $clickable={isClickable}
                                    onClick={isClickable ? () => goToStep(i) : undefined}
                                >
                                    <StepperCircle $active={isActive} $done={isDone}>
                                        {i + 1}
                                    </StepperCircle>
                                    <StepperLabel $active={isActive} $done={isDone}>
                                        {s.label}
                                    </StepperLabel>
                                    {i < STEPS.length - 1 && <StepperConnector />}
                                </StepWrapper>
                            );
                        })}
                    </StepperWrap>

                    {currentStepIndex === 0 && (
                        <ContentCard>
                            <CardTitle>Select your path</CardTitle>
                            <CardSubtitle>
                                Choose how you want to get started. You can always change this later.
                            </CardSubtitle>
                            <PathOptionsWrap>
                                <PathOptionCard
                                    type="button"
                                    $selected={type === 'client'}
                                    onClick={() => setType('client')}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <PathOptionIcon $selected={type === 'client'}>
                                        <User size={24} strokeWidth={1.5} />
                                    </PathOptionIcon>
                                    <PathOptionTitle>Client</PathOptionTitle>
                                    <PathOptionDesc>I&apos;m here to use services and get things done.</PathOptionDesc>
                                </PathOptionCard>
                                <PathOptionCard
                                    type="button"
                                    $selected={type === 'company'}
                                    onClick={() => setType('company')}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <PathOptionIcon $selected={type === 'company'}>
                                        <Briefcase size={24} strokeWidth={1.5} />
                                    </PathOptionIcon>
                                    <PathOptionTitle>Company</PathOptionTitle>
                                    <PathOptionDesc>
                                        I own or represent a company and want to manage projects.
                                    </PathOptionDesc>
                                </PathOptionCard>
                            </PathOptionsWrap>
                            <ProceedButtonWrap>
                                <ProceedButtonStyled
                                    type="button"
                                    $disabled={!type}
                                    disabled={!type}
                                    onClick={() => type && handleSelect(type)}
                                >
                                    Proceed
                                </ProceedButtonStyled>
                            </ProceedButtonWrap>
                        </ContentCard>
                    )}

                    {currentStepIndex >= 1 && (
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                            requiredMark={false}
                            style={{
                                width: '100%',
                                maxWidth: 560,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            <Form.Item name="planId" hidden>
                                <input type="hidden" aria-hidden="true" />
                            </Form.Item>

                            {currentStepIndex === 1 && (
                                <ContentCard>
                                    <FormTitle>
                                        {type === 'client' ? 'Set up your business' : 'Set up your company'}
                                    </FormTitle>
                                    <FormSubtitle>Enter your details, then continue to choose a plan.</FormSubtitle>
                                    <Form.Item
                                        name="name"
                                        label={nameLabel}
                                        rules={[{ required: true, message: `Please enter ${nameLabel.toLowerCase()}` }]}
                                    >
                                        <Input
                                            placeholder={nameLabel}
                                            $maxWidth="100%"
                                            $size="0.85rem"
                                            $padding="12px 14px"
                                            $weight={400}
                                            $background="transparent"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        name="description"
                                        label={descLabel}
                                        rules={[{ required: true, message: `Please enter ${descLabel.toLowerCase()}` }]}
                                    >
                                        <TextArea
                                            placeholder={descLabel}
                                            rows={4}
                                            $minHeight="120px"
                                            $size="0.85rem"
                                            $padding="12px 14px"
                                            $weight={400}
                                            $background="transparent"
                                        />
                                    </Form.Item>
                                    <ProceedButtonWrap>
                                        <ProceedButtonStyled type="button" onClick={handleDetailsContinue}>
                                            Continue
                                        </ProceedButtonStyled>
                                    </ProceedButtonWrap>
                                </ContentCard>
                            )}

                            {currentStepIndex === 2 && (
                                <ContentCard>
                                    <CardTitle>Choose a plan</CardTitle>
                                    <CardSubtitle>Select a plan below, then click Complete.</CardSubtitle>
                                    <PlanCardsWrap>
                                        {plansLoading ? (
                                            <div
                                                style={{
                                                    padding: 24,
                                                    textAlign: 'center',
                                                    color: '#6b7280',
                                                    width: '100%',
                                                }}
                                            >
                                                Loading plans...
                                            </div>
                                        ) : (
                                            plans.map((p, idx) => {
                                                const theme = PLAN_CARD_COLORS[idx % PLAN_CARD_COLORS.length];
                                                const amount = p.afterDiscountAmount ?? p.amount ?? 0;
                                                const isFree =
                                                    amount === 0 ||
                                                    p.key === 'free' ||
                                                    String(p.name || '')
                                                        .toLowerCase()
                                                        .includes('free');
                                                const description = p.details || p.description || '';
                                                const features =
                                                    Array.isArray(p.features) && p.features.length > 0
                                                        ? p.features
                                                        : [];
                                                return (
                                                    <PlanCard
                                                        key={p.id}
                                                        type="button"
                                                        $selected={watchedPlanId === p.id}
                                                        onClick={() => form.setFieldValue('planId', p.id)}
                                                        whileTap={{ scale: 0.98 }}
                                                    >
                                                        <PlanCardHeader $color={theme?.header}>
                                                            {p.name?.toUpperCase() || 'PLAN'}
                                                        </PlanCardHeader>
                                                        <PlanCardBody>
                                                            <PlanCardPrice $color={theme?.text}>
                                                                {isFree ? 'FREE' : `$${amount}`}
                                                                {!isFree && p.cycle === 'monthly' && (
                                                                    <PlanCardPriceUnit> /mo</PlanCardPriceUnit>
                                                                )}
                                                                {!isFree && p.cycle === 'yearly' && (
                                                                    <PlanCardPriceUnit> /yr</PlanCardPriceUnit>
                                                                )}
                                                            </PlanCardPrice>
                                                            {description && (
                                                                <PlanCardDescription>{description}</PlanCardDescription>
                                                            )}
                                                            {features.length > 0 && (
                                                                <PlanCardFeatures>
                                                                    {features.map((f, i) => (
                                                                        <PlanCardFeature key={i}>{f}</PlanCardFeature>
                                                                    ))}
                                                                </PlanCardFeatures>
                                                            )}
                                                        </PlanCardBody>
                                                    </PlanCard>
                                                );
                                            })
                                        )}
                                    </PlanCardsWrap>
                                    {watchedPlanId && (
                                        <ProceedButtonWrap>
                                            <ProceedButtonStyled type="primary" htmlType="submit" loading={loading}>
                                                Complete
                                            </ProceedButtonStyled>
                                        </ProceedButtonWrap>
                                    )}
                                </ContentCard>
                            )}
                        </Form>
                    )}
                </OnboardingInner>
            )}

            {step === 'success' && type && (
                <SuccessScreen initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
                    <SuccessWrap>
                        <SuccessIcon
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.1 }}
                        >
                            <PartyPopper size={44} strokeWidth={2} />
                        </SuccessIcon>
                        <SuccessTitle>Congratulations!</SuccessTitle>
                        <SuccessText>Your account is all set. Redirecting you to the dashboard...</SuccessText>
                    </SuccessWrap>
                </SuccessScreen>
            )}
        </PageRoot>
    );
};

export default Onboarding;
